import React, { useRef, useState, useCallback } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  importBeamsFromCSV,
  exportBeamsToCSV,
  copyBeamsToClipboard,
  processExcelFile,
  createFileValidationError,
  generateFilename,
} from "@/utilities/CSVUtils";

import {
  TrashIcon,
  ArrowUpOnSquareIcon,
  ArrowDownOnSquareIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/solid";

import { useModelStore } from "@/store/useModelStore";
import { usePropertyStore } from "@/store/usePropertyStore";
import { useBeamSectionStore } from "@/hooks/useBeamSection";
import { calculateBeamLength } from "@/utilities/BeamCenterUtils";

import { CSVImportChoiceDialog } from "../dialogs/CSVImportChoiceDialog";
import { DuplicateGroupReviewDialog } from "../dialogs/DuplicateGroupReviewDialog";
import { useEnhancedCSVImport } from "@/hooks/useEnhancedCSVImport";

import {
  TableLayout,
  TableColumn,
} from "../../workspace/components/ui/TableLayout";

export const BeamsDataTable: React.FC = () => {
  const beams = useModelStore((state) => state.beams);
  const nodes = useModelStore((state) => state.nodes);
  const deleteBeam = useModelStore((state) => state.deleteBeam);
  const addBeam = useModelStore((state) => state.addBeam);
  const toggleBeamSelection = useModelStore(
    (state) => state.toggleBeamSelection
  );
  const selectedBeamIds = useModelStore((state) => state.selectedBeamIds);
  const updateElement = useModelStore((state) => state.updateElement);
  const setBeams = useModelStore((state) => state.setBeams);

  const properties = usePropertyStore((state) => state.properties);
  const propertyIds = ["-", ...properties.map((p) => p.P_ID)];

  const fileInputRef = useRef<HTMLInputElement>(null);

  // ---------------- NEW BEAM STATE ----------------
  const [newBeam, setNewBeam] = useState({ startNodeId: "", endNodeId: "" });

  // ---------------- ADD NEW BEAM HANDLER ----------------
  const handleAddBeam = useCallback(() => {
    const startId = newBeam.startNodeId.trim();
    const endId = newBeam.endNodeId.trim();

    // Validation 1: Check if both fields are provided
    if (!startId || !endId) {
      alert("Please provide both Start Node and End Node IDs.");
      return;
    }

    // Validation 2: Check if both are numeric (node IDs are numbers)
    if (!/^\d+$/.test(startId) || !/^\d+$/.test(endId)) {
      alert("Node IDs must be numeric only.");
      return;
    }

    // Validation 3: Start and end node can't be the same
    if (startId === endId) {
      alert("Start and End nodes must be different.");
      return;
    }

    // Validation 4: Check if both nodes exist in the node table
    const validNodeIds = new Set(nodes.map((n) => n.id));
    
    if (!validNodeIds.has(startId)) {
      alert(`Start Node "${startId}" does not exist in the node table.`);
      return;
    }

    if (!validNodeIds.has(endId)) {
      alert(`End Node "${endId}" does not exist in the node table.`);
      return;
    }

    // Validation 5: Check if beam already exists between these nodes (bidirectional)
    const beamExists = beams.some(
      (b) =>
        (b.startNodeId === startId && b.endNodeId === endId) ||
        (b.startNodeId === endId && b.endNodeId === startId)
    );

    if (beamExists) {
      alert(`A beam already exists between nodes ${startId} and ${endId}.`);
      return;
    }

    // All validations passed - create the beam
    addBeam(startId, endId);
    
    // Reset the form
    setNewBeam({ startNodeId: "", endNodeId: "" });
  }, [newBeam, nodes, beams, addBeam]);

  // ---------------- CSV IMPORT HOOK ----------------
  const csvImport = useEnhancedCSVImport(
    beams,
    (importedBeams, mode) => {
      setBeams(importedBeams);
      alert(
        `Import completed successfully! ${importedBeams.length} beams ${
          mode === "replace" ? "replaced" : "total"
        }.`
      );
    },
    "beams"
  );

  // ---------------- VIRTUALIZER ----------------
  const parentRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: beams.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 44,
    overscan: 3,
  });

  // Auto-scroll to selected beam
  React.useEffect(() => {
    if (!selectedBeamIds.length) return;
    const target = selectedBeamIds[0];
    const index = beams.findIndex((b) => b.id === target);
    if (index >= 0) {
      rowVirtualizer.scrollToIndex(index, { align: "center" });
    }
  }, [selectedBeamIds, beams]);

  // ---------------- INLINE EDIT STATE (start/end node id) ----------------
  const [editState, setEditState] = useState<{
    beamId: string;
    field: "start" | "end";
    value: string;
  } | null>(null);

  const startEdit = (
    e: React.MouseEvent,
    beamId: string,
    field: "start" | "end",
    current: string
  ) => {
    e.stopPropagation();
    setEditState({ beamId, field, value: current });
  };

  const commitEdit = () => {
    if (!editState) return;

    const newId = editState.value.trim();
    const validNodeIds = new Set(nodes.map((n) => n.id));

    if (!validNodeIds.has(newId)) {
      alert(`Node ${newId} does not exist.`);
      return setEditState(null);
    }

    const beam = beams.find((b) => b.id === editState.beamId);
    if (!beam) return setEditState(null);

    const other =
      editState.field === "start" ? beam.endNodeId : beam.startNodeId;

    if (newId === other) {
      alert("Start and end nodes must be different.");
      return setEditState(null);
    }

    updateElement(beam.id, {
      [editState.field === "start" ? "startNodeId" : "endNodeId"]: newId,
    });

    setEditState(null);
  };

  // ---------------- MATERIAL & P_ID ----------------
  const handlePIDChange = (beamId: string, newPID: string) => {
    updateElement(beamId, { P_ID: newPID });

    const propertyStore = usePropertyStore.getState();
    const beamSectionStore = useBeamSectionStore.getState();

    if (newPID === "-") {
      beamSectionStore.removeSectionsForBeams([beamId]);
      return;
    }

    const prop = propertyStore.getProperty(newPID);
    if (prop && !prop.beamIds.includes(beamId)) {
      propertyStore.updateProperty(newPID, {
        beamIds: [...prop.beamIds, beamId],
      });
    }

    propertyStore.applyPropertyToBeams(newPID, [beamId]);
  };

  // ---------------- CSV EXPORT ----------------
  const handleExport = () => {
    const filename = generateFilename("beams");
    exportBeamsToCSV(beams, { filename });
  };

  // ---------------- CSV IMPORT ----------------
  const handleFileSelected = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const ext = file.name.split(".").pop()?.toLowerCase();
    try {
      let result;

      const existingNodeIds = nodes.map((n) => n.id);

      if (ext === "csv") {
        result = await importBeamsFromCSV(file, existingNodeIds);
      } else if (ext === "xlsx" || ext === "xls") {
        const excel = await processExcelFile(file);
        if (excel.success) {
          const transformed = excel.data.map((beam: any, i: number) => ({
            id: beam.id?.toString() || (i + 1).toString(),
            startNodeId:
              beam.startNodeId?.toString() ||
              beam.S_Node?.toString() ||
              beam.startNode?.toString() ||
              "",
            endNodeId:
              beam.endNodeId?.toString() ||
              beam.E_Node?.toString() ||
              beam.endNode?.toString() ||
              "",
            material: beam.material || "Steel",
            isSelected: false,
            color: "#4dabf7",
          }));

          result = {
            ...excel,
            data: transformed,
            preview: transformed.slice(0, 5),
          };
        } else {
          result = excel;
        }
      } else {
        alert(createFileValidationError(file, ["csv", "xlsx", "xls"]));
        return;
      }

      csvImport.handleFileImported(result);
    } catch (err) {
      alert("Error processing file.");
    }

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ---------------- COPY ----------------
  const handleCopy = async () => {
    if (await copyBeamsToClipboard(beams)) {
      alert("Beams copied!");
    }
  };

  // -------------------------------------------------
  // TABLE HEADER CONFIG (theader)
  // -------------------------------------------------
  const theader: TableColumn[] = [
    { colname: "B_ID", className: "w-1/7", field: "id" },

    {
      colname: "S_Node",
      className: "w-1/7",
      render: (beam) =>
        editState?.beamId === beam.id && editState?.field === "start" ? (
          <input
            type="text"
            autoFocus
            className="w-full h-8 px-2 border rounded"
            value={editState!.value}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) =>
              setEditState((p) =>
                p ? { ...p, value: e.target.value } : p
              )
            }
            onBlur={commitEdit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitEdit();
              if (e.key === "Escape") setEditState(null);
            }}
          />
        ) : (
          <span
            className="inline-flex items-center h-8 w-full px-2 bg-gray-50 rounded cursor-text"
            onClick={(e) =>
              startEdit(e, beam.id, "start", beam.startNodeId)
            }
          >
            {beam.startNodeId}
          </span>
        ),
    },

    {
      colname: "E_Node",
      className: "w-1/7",
      render: (beam) =>
        editState?.beamId === beam.id && editState?.field === "end" ? (
          <input
            type="text"
            autoFocus
            className="w-full h-8 px-2 border rounded"
            value={editState!.value}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) =>
              setEditState((p) =>
                p ? { ...p, value: e.target.value } : p
              )
            }
            onBlur={commitEdit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitEdit();
              if (e.key === "Escape") setEditState(null);
            }}
          />
        ) : (
          <span
            className="inline-flex items-center h-8 w-full px-2 bg-gray-50 rounded cursor-text"
            onClick={(e) => startEdit(e, beam.id, "end", beam.endNodeId)}
          >
            {beam.endNodeId}
          </span>
        ),
    },

    {
      colname: "Length",
      className: "w-1/7",
      render: (beam) => calculateBeamLength(beam, nodes).toFixed(2),
    },

    { colname: "Material", className: "w-1/7", field: "material" },

    {
      colname: "P_ID",
      className: "w-1/7",
      render: (beam) => (
        <select
          value={beam.P_ID || "-"}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => handlePIDChange(beam.id, e.target.value)}
          className="w-full h-8 px-2 border rounded bg-white text-sm"
        >
          {propertyIds.map((pid) => (
            <option key={pid}>{pid}</option>
          ))}
        </select>
      ),
    },

    {
      colname: "Del",
      className: "w-[60px]",
      render: (beam) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            deleteBeam(beam.id);
          }}
          className="text-red-500 hover:text-red-700"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      ),
    },
  ];

  return (
    <>
      <TableLayout
        title="Beams"
        theader={theader}
        tdata={beams}
        actions={
          <>
            {/* Import */}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-1 hover:bg-gray-600 rounded"
            >
              <ArrowDownOnSquareIcon className="h-5 w-5" />
            </button>

            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept=".csv,.xlsx,.xls"
              onChange={handleFileSelected}
            />

            {/* Export */}
            <button
              onClick={handleExport}
              className="p-1 hover:bg-gray-600 rounded"
            >
              <ArrowUpOnSquareIcon className="h-5 w-5" />
            </button>

            {/* Copy */}
            <button
              onClick={handleCopy}
              className="p-1 hover:bg-gray-600 rounded"
            >
              <ClipboardDocumentIcon className="h-5 w-5" />
            </button>
          </>
        }
        extra={
          <div className="flex items-center gap-2 py-1">
            <span className="font-semibold text-gray-700 text-sm min-w-[50px]">New</span>
            <div className="flex items-center gap-2">
              <div className="flex flex-col">
                <label className="text-[10px] text-gray-600 mb-0.5">S_Node</label>
                <input
                  type="text"
                  value={newBeam.startNodeId}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Only allow numeric input
                    if (value === "" || /^\d+$/.test(value)) {
                      setNewBeam((prev) => ({ ...prev, startNodeId: value }));
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAddBeam();
                  }}
                  placeholder="Start Node"
                  className="w-28 px-1.5 py-0.5 border rounded text-xs text-black focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-[10px] text-gray-600 mb-0.5">E_Node</label>
                <input
                  type="text"
                  value={newBeam.endNodeId}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Only allow numeric input
                    if (value === "" || /^\d+$/.test(value)) {
                      setNewBeam((prev) => ({ ...prev, endNodeId: value }));
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAddBeam();
                  }}
                  placeholder="End Node"
                  className="w-28 px-1.5 py-0.5 border rounded text-xs text-black focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <button
                onClick={handleAddBeam}
                className="mt-3.5 px-3 py-0.5 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-xs font-medium"
              >
                Add Beam
              </button>
            </div>
          </div>
        }
        onRowClick={(beam) => toggleBeamSelection(beam.id)}
        getRowClassName={(beam) =>
          selectedBeamIds.includes(beam.id)
            ? "selected-table-row bg-blue-100"
            : ""
        }
      />

      {/* Dialogs */}
      <CSVImportChoiceDialog
        isOpen={csvImport.showChoiceDialog}
        onClose={csvImport.cancelImport}
        onChoice={csvImport.handleImportChoice}
        title="Import Beams"
        itemCount={csvImport.importResult.data.length}
        itemType="beams"
      />

      <DuplicateGroupReviewDialog
        isOpen={csvImport.showDuplicateReview}
        onClose={csvImport.cancelImport}
        onFinish={csvImport.handleDuplicateResolution}
        groups={csvImport.duplicateGroups?.groups || []}
        itemType="beams"
        title="Review Duplicate Beams"
      />

      {csvImport.hasError && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded shadow">
            <h2>Error</h2>
            <button onClick={csvImport.cancelImport}>OK</button>
          </div>
        </div>
      )}
    </>
  );
};
